import { FC, memo } from 'react';
import { FormProvider } from 'react-hook-form';
import { Button, Stack } from '@chakra-ui/react';

import { FormField, Select } from '~/core/shared/components';
import { Dialog } from '~/core/shared/components/Dialog.component';
import { useIndividualPluginModal } from './hooks';
import { IndividualPluginModalProps } from './types';

/**
 * Runs a tenant plugin against one individual on the deal. The tenant action config supplies the copy and the plugin
 * through `contentComponent.componentProps`, and the backend decides which individuals are offered.
 */
const IndividualPluginModalComponent: FC<IndividualPluginModalProps> = ({
  header,
  description,
  ...runProps
}) => {
  const {
    applicantOptions,
    form,
    isLoadingApplicants,
    isSubmitting,
    modalApi,
    onSubmit,
  } = useIndividualPluginModal(runProps);

  return (
    <Dialog
      header={header}
      onOpenChange={modalApi.onOpenChange}
      open={modalApi.open}
      subHeader={description}
    >
      <FormProvider {...form}>
        <Stack asChild gap={4}>
          <form onSubmit={onSubmit}>
            <FormField
              component={Select}
              label="Applicant"
              name="individualId"
              options={applicantOptions}
              placeholder="Select applicant"
              required
              variant="outline"
            />

            <Stack direction="row" justifyContent="end">
              <Button
                colorPalette="gray"
                fontSize="md"
                fontWeight="normal"
                onClick={modalApi.onClose}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                colorPalette="primary"
                disabled={isLoadingApplicants}
                fontSize="md"
                fontWeight="bold"
                loading={isSubmitting}
                onClick={onSubmit}
                type="button"
                variant="solid"
              >
                Submit
              </Button>
            </Stack>
          </form>
        </Stack>
      </FormProvider>
    </Dialog>
  );
};

export const IndividualPluginModal = memo(IndividualPluginModalComponent);
